import { useMediaQuery } from '@uidotdev/usehooks';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import useAuth from 'hooks/useAuth';
import { useGetUserDataQuery } from 'store/api/user';

/**
 * The colours the user's stored theme resolves to: `DARK` is dark, `LIGHT` or unset is light, and `SYSTEM` follows the
 * device as it changes. It reads the current-user query, as `useUiDesign` does, so a saved change applies at once; while
 * impersonating it is the other user's theme. The design is not read here: the layout paints dark in the new design
 * only.
 */
export function useUiTheme(): 'dark' | 'light' {
    const { user } = useAuth();
    const { data } = useGetUserDataQuery(undefined, { skip: !user });
    const deviceDark = useMediaQuery('(prefers-color-scheme: dark)');
    const theme = data?.preferences?.newDesignTheme;
    return theme === 'DARK' || (theme === 'SYSTEM' && deviceDark) ? 'dark' : 'light';
}

function onThemeAttributeChange(onChange: () => void) {
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
}

function documentTheme(): 'dark' | 'light' {
    return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * The theme the page is painted in, read off `<html data-theme>`, which the signed-in layout sets. For what renders
 * outside that layout, as the toasts do above the auth provider: light wherever the layout has not marked the page.
 */
export function useDocumentTheme(): 'dark' | 'light' {
    return useSyncExternalStore(onThemeAttributeChange, documentTheme);
}

/**
 * An `--elev-*` shadow as `scope` (the page by default) paints it, read again as the theme changes. For the `style` of a
 * box a layout animation moves: Motion corrects a shadow it is given as numbers for the scale it draws the move with,
 * and turns a `var()` into no shadow at all. A box under its own `data-design` passes the element that carries it.
 */
export function useElevation(name: string, scope?: Element | null): string {
    const theme = useDocumentTheme();
    // Read once per theme: a style read in every render recomputes the page's styles whenever they are dirty.
    return useMemo(
        () =>
            getComputedStyle(scope ?? document.documentElement)
                .getPropertyValue(`--elev-${name}`)
                .trim(),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` changes what the read returns
        [name, scope, theme]
    );
}

/** Runs `change` with transitions off until the next frame, so a theme switch paints its new colours at once. */
function withoutTransitions(change: () => void) {
    const style = document.createElement('style');
    style.textContent = '* { transition: none !important; }';
    document.head.append(style);
    change();
    // The forced style read computes the new colours while the rule holds, so none of them starts a transition.
    getComputedStyle(document.documentElement).getPropertyValue('color');
    requestAnimationFrame(() => style.remove());
}

/**
 * Marks the document dark, for the new design's dark block; classic never takes it. Each change of it paints without
 * transitions, or every button and row would fade from one palette to the other.
 */
export function useDocumentThemeMark(isNew: boolean, theme: 'dark' | 'light') {
    const dark = isNew && theme === 'dark';
    useEffect(() => {
        if (!dark) return;
        const root = document.documentElement;
        withoutTransitions(() => {
            root.dataset.theme = 'dark';
        });
        return () => {
            withoutTransitions(() => {
                delete root.dataset.theme;
            });
        };
    }, [dark]);
}
